import { useMemo } from 'react';

/**
 * Confete de fim de fase (§22.7).
 *
 * Papel picado desenhado com `<span>` e uma animação de CSS — sem biblioteca e
 * sem `<canvas>`. São trinta elementos que vivem menos de dois segundos, e
 * §16 conta cada byte do pacote: uma dependência de confete custaria mais que
 * o jogo inteiro de sugestões.
 *
 * Cada peça sorteia trajetória, giro, atraso e duração uma única vez, na
 * montagem. Re-sortear a cada render faria a explosão tremer no lugar em vez
 * de voar, e o `useMemo` é o que a mantém no ar.
 *
 * Este é o único sorteio da aplicação que não passa pela semente da sessão
 * (ADR-0004), e pode: nada aqui é jogo. Se o confete fosse determinístico, a
 * segunda fase explodiria exatamente igual à primeira.
 *
 * `aria-hidden` inteiro: quem usa leitor de tela recebe o anúncio da fase por
 * `aria-live` (§13), e trinta divs sem conteúdo só atrapalhariam.
 */

const PECAS = 30;

/** As cores da casa. Confete genérico entregaria outro jogo. */
const CORES = ['var(--ponte)', 'var(--realce)', 'var(--tinta-media)', 'var(--ponte-borda)'];

interface Peca {
  readonly x: number;
  readonly y: number;
  readonly giro: number;
  readonly atrasoMs: number;
  readonly duracaoMs: number;
  readonly largura: number;
  readonly altura: number;
  readonly cor: string;
}

function sortearPecas(): readonly Peca[] {
  return Array.from({ length: PECAS }, (): Peca => {
    const angulo = Math.random() * Math.PI * 2;
    // Raios diferentes por peça: uma explosão de raio único vira um anel.
    const alcance = 60 + Math.random() * 170;
    return {
      x: Math.cos(angulo) * alcance,
      // A queda soma ao voo: sobe torto, cai reto, como papel de verdade.
      y: Math.sin(angulo) * alcance * 0.7 + 60 + Math.random() * 120,
      giro: Math.random() * 900 - 450,
      atrasoMs: Math.random() * 140,
      duracaoMs: 1000 + Math.random() * 800,
      largura: 5 + Math.random() * 5,
      altura: 9 + Math.random() * 8,
      cor: CORES[Math.floor(Math.random() * CORES.length)] ?? CORES[0]!,
    };
  });
}

export function Confete() {
  const pecas = useMemo(sortearPecas, []);

  return (
    <div className="confete" aria-hidden="true">
      {pecas.map((peca, i) => (
        <span
          key={i}
          className="confete__peca"
          style={
            {
              '--confete-x': `${peca.x.toFixed(1)}px`,
              '--confete-y': `${peca.y.toFixed(1)}px`,
              '--confete-giro': `${peca.giro.toFixed(0)}deg`,
              '--confete-atraso': `${peca.atrasoMs.toFixed(0)}ms`,
              '--confete-duracao': `${peca.duracaoMs.toFixed(0)}ms`,
              '--confete-largura': `${peca.largura.toFixed(1)}px`,
              '--confete-altura': `${peca.altura.toFixed(1)}px`,
              '--confete-cor': peca.cor,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}
